import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import { FormikControl } from '..';
import {
    NETLIFY_FUNCTIONS_PATH,
    CTA_CLICK_SMS_OPT_IN,
    callVerifyHuman,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    getLeadsCaptureEcho,
    safeFullStory as FullStory,
} from '../../../utils';
import { contactInitialValues as initialValues, contactSchema } from '.';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import { useScrollToAnchor } from '../../../hooks/builder/useScrollToAnchor';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    LocationsDropdownWrapper,
    MarketingConsentMsg,
} from '../shared';

export function ContactForm({ siteData }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('contactForm', executeRecaptcha);
        return user;
        // Do whatever you want with the token
    }, [executeRecaptcha]);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('contact-form-wrapper');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            const user = await handleReCaptchaVerify();
            if (user === 'bot') {
                helpers.setErrors('bot', true);
                throw new Error('Bot detected');
            } else {
                // Validate phone and email with PossibleNow
                const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                    values.phone,
                    values.email
                );

                const formData = {
                    ...values,
                    updates: true, // Always true for TCPA compliance
                    marketing_sms_optout: !CTA_CLICK_SMS_OPT_IN,
                    cloudamp__data__c: localStorage.getItem('cloudamp__data__c') || '',
                    formType: 'contact',
                    fbp,
                    fbc,
                    experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                    experiment_user_ID,
                    phoneFound,
                    wireless,
                    emailFound,
                    result,
                    flags,
                };
                const response = await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);

                // Store form values for the attribution survey. The handler returns submissionId
                // and TU IDs when leads-capture is on; the survey echoes them so its answer
                // updates the original lead instead of creating a second one.
                setFormValues({ ...formData, ...getLeadsCaptureEcho(response) });

                // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                    syncPardotTracking(values);
                }

                // Show attribution survey instead of immediate success
                setShowAttributionSurvey(true);

                helpers.setSubmitting(false);
                helpers.setStatus('Success');
                // FullStory Event Tracking
                FullStory('trackEvent', {
                    name: 'Contact Form - Success',
                    properties: {
                        name: 'Contact',
                        user_email: values.email,
                        sms_optin: CTA_CLICK_SMS_OPT_IN,
                    },
                });

                helpers.resetForm();
            }
        } catch (error) {
            console.error('Form submission error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Form submission error';

            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);
            setShowAttributionSurvey(false);

            // FullStory Event Tracking with enhanced error details
            FullStory('trackEvent', {
                name: 'Contact Form - Error',
                properties: {
                    name: 'Contact',
                    user_email: values.email,
                    error_message: errorMessage,
                    error_details: axios.isAxiosError(error)
                        ? {
                              status: error.response?.status,
                              statusText: error.response?.statusText,
                          }
                        : 'Non-axios error',
                },
            });

            // Log that Pardot tracking was skipped due to form submission failure
            FullStory('trackEvent', {
                name: 'Contact Form - Pardot Sync Skipped',
                properties: {
                    user_email: values.email,
                    error_message: errorMessage,
                    reason: 'Actual Form submission failed',
                },
            });
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);

        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <div className="contact-form-wrapper">
            <Formik
                initialValues={initialValues}
                validationSchema={contactSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {(formik) => {
                    if (formik.isSubmitting) {
                        return <FormSubmitting />;
                    }

                    // Handle error state
                    if (formik.status === 'Failed') {
                        return <FormFailed />;
                    }

                    if (formValues) {
                        // At this point the form is submitted
                        if (showAttributionSurvey) {
                            return <AttributionSurvey formValues={formValues} onComplete={handleAttributionComplete} />;
                        }
                        return <FormSuccess />;
                    }

                    return (
                        <Form id="contact-form" className="w-100 pb-4 fs-mask">
                            {!showAttributionSurvey && !formValues && !formik.isSubmitting && (
                                <h2 className="milan-text-primary h3 fw-normal text-center">Or Send Us A Question</h2>
                            )}
                            <div className="row justify-content-center mx-auto pt-md-2 gx-2">
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="input"
                                        name="first_name"
                                        placeholder="First Name"
                                        type="text"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="input"
                                        name="last_name"
                                        placeholder="Last Name"
                                        type="text"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                            </div>
                            <div className="row justify-content-center mx-auto gx-2">
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="phoneInput"
                                        name="phone"
                                        placeholder="Phone Number"
                                        type="tel"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <FormikControl
                                        control="emailInput"
                                        name="email"
                                        placeholder="Your Email"
                                        role="input"
                                        aria-required
                                    />
                                </div>
                                <div className="col-md-12 mb-3">
                                    <label htmlFor="location">Location</label>
                                    <LocationsDropdownWrapper {...{ siteData, formik }} fieldName="location" />
                                </div>
                            </div>
                            <div className="row justify-content-center mx-auto overflow-hidden gx-2">
                                <div className="mb-2">
                                    <FormikControl
                                        control="textarea"
                                        name="description"
                                        placeholder="Hello..."
                                        rows="3"
                                        role="input"
                                    />
                                </div>
                            </div>

                            <div className="row justify-content-center my-3">
                                <div className="col-12 lh-1">
                                    <MarketingConsentMsg
                                        cta={
                                            <>
                                                Submit <sup>*</sup>
                                            </>
                                        }
                                    />
                                </div>
                            </div>

                            <div id="actions-btns" className="row justify-content-center mt-4">
                                <div className="col-10 col-lg-6 text-center">
                                    <button
                                        className="milan-btn milan-red-btn w-100"
                                        type="submit"
                                        disabled={!formik.isValid || formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                Submit <sup>*</sup>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}

export default ContactForm;
